import { Injectable } from '@angular/core';
import { StreamChat, Channel, MessageResponse } from 'stream-chat';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ChatService as ChatApiService } from '@vestiaire/api-sdk-angular';
import { map, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';
import { getUser } from '@app/core/user/store/reducers';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  streamChat: StreamChat;
  connected$ = new BehaviorSubject<boolean>(false);
  connectionState$ = this.connected$.asObservable();

  // We use the following as a "hook" before sending a message.
  // @see ChatContent subscribe
  private _sendingMessage = new Subject<string>();
  sendingMessage = this._sendingMessage.asObservable();

  constructor(private chatApiService: ChatApiService, private store: Store<IAppState>, private router: Router) {}

  init() {
    this.streamChat = new StreamChat(environment.streamApiKey);
    this.streamChat.setBaseURL('https://chat-proxy-dublin.stream-io-api.com');
    this.store.pipe(select(getUser), filter(Boolean)).subscribe({
      next: ({ id }) => this.connect(id)
    });
  }

  async connect(userID: string, token?: string) {
    const user = { id: userID };
    const tokenProvider = () => {
      console.log('Refreshed chat token at ', new Date());
      return this.chatApiService
        .refreshToken()
        .pipe(map(res => res.data.userToken.token))
        .toPromise();
    };

    this.streamChat
      .setUser(user, tokenProvider)
      .then(_ => this.connected$.next(true))
      .catch(_ => this.connected$.next(false));
  }

  createChannel() {
    // Create channel from Api
  }

  async joinChannel(channelId: string): Promise<Channel> {
    const query = { id: channelId, members: { $in: [this.streamChat.user.id] } };
    const sort = { last_message_at: -1 };
    const options = {
      watch: true,
      state: true
    };

    const channels = await this.streamChat.queryChannels(query, sort, options);

    if (channels && channels.length && channels[0].id === channelId) {
      return Promise.resolve(channels[0]);
    }

    return Promise.reject(`Cannot find channelId: ${channelId}`);
  }

  // when the user is blocked by his interlocutor (chanel banned) but can chat with other members
  async getBlockedMembers(channel: Channel): Promise<string[]> {
    try {
      const { members } = await channel.queryMembers({ banned: true });
      return members.map(member => member.user.id);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  // when the user is globally banned from moderation and cannot chat with any member anymore
  async getBannedMembers(channel: Channel): Promise<string[]> {
    try {
      const { members: banned } = await channel.queryMembers({});
      return banned.filter(member => member.user.banned === true).map(member => member.user.id);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  banUserFromChannel(channel: Channel, userId: string) {
    return channel.banUser(userId, {});
  }

  unbanUserFromChannel(channel: Channel, userId: string) {
    return channel.unbanUser(userId);
  }

  getLastMessages(channel: Channel): MessageResponse[] {
    return (channel.state.messages as unknown) as MessageResponse[];
  }

  getMessages(channel: Channel, beforeId?: string, limit = 25): Promise<MessageResponse[]> {
    return channel
      .query({
        messages: {
          limit,
          id_lt: beforeId
        }
      })
      .then(({ messages }) => messages || []);
  }

  onSendMessage(text: string) {
    this._sendingMessage.next(text);
  }

  sendMessage(channel: Channel, text: string): Promise<MessageResponse> {
    return channel
      .sendMessage({
        text
      })
      .then(({ message }) => message);
  }

  translateMessage(messageID: string, locale: string): Promise<MessageResponse> {
    return this.streamChat.translateMessage(messageID, locale).then(res => res.message);
  }

  async markAsRead(channel: Channel) {
    await channel.markRead();
  }

  async logout() {
    if (this.streamChat) {
      try {
        await this.streamChat.disconnect();
        this.connected$.next(false);
        this.router.navigate(['/']);
      } catch (error) {
        console.error(error);
      }
    }
  }
}
